import { useMemo, useState, useLayoutEffect, useRef, useCallback } from "react";
import { useStoryBibleStore } from "../stores/storyBibleStore";
import { useCommentStore } from "../stores/commentStore";
import useEditPluginInstallations from "./useEditPluginInstallations";
import { estimateFeedbackCredits, formatCreditEstimate, CreditEstimate } from "../shared/feedbackCreditEstimate";
import { FEEDBACK_CATEGORY_KEYS, FeedbackPlugin } from "../shared/feedbackCategories";

import Auth from "../Auth";
import { getFeedbackSelectionPayload } from "../components/TiptapDocumentSB/feedbackRequestPayload";

class FeedbackSettingsUnavailableError extends Error {
  constructor(failed: boolean) {
    super(failed
      ? "Could not load Feedback settings. Please try again."
      : "Feedback settings are still loading. Please try again.");
  }
}

export const getFeedbackEstimateErrorMessage = (error: unknown): string => {
  if (error instanceof FeedbackSettingsUnavailableError) return error.message;
  if (error instanceof Error && error.name === "AuthedFetchError" && "responseFailureKind" in error && error.responseFailureKind === "http-error") {
    const message = error.message.trim();
    if (message && message !== "Server problem.") return message;
  }
  return "Could not estimate Feedback. Please try again.";
};

// Extend Window to include tiptap
declare global {
  interface Window {
    tiptap?: {
      on?: (event: string, callback: () => void) => void;
      off?: (event: string, callback: () => void) => void;
      getText: () => string;
      isEditable: boolean;
      setEditable: (editable: boolean) => void;
      getJSON: () => unknown;
    };
  }
}

/**
 * Count the number of distinct categories that have at least one enabled plugin.
 */
export function countEnabledCategories(installations: FeedbackPlugin[] | undefined): number {
  if (!installations || installations.length === 0) return 0;

  const enabledCategories = new Set<string>();
  for (const plugin of installations) {
    if (plugin.enabled && plugin.category && FEEDBACK_CATEGORY_KEYS.has(plugin.category)) {
      enabledCategories.add(plugin.category);
    }
  }
  return enabledCategories.size;
}

/**
 * Simple word count from text — counts whitespace-separated tokens.
 */
export function countWordsFromText(text: string): number {
  if (!text || text.trim().length === 0) return 0;
  return text.trim().split(/\s+/).length;
}

/**
 * Get the document word count from the TipTap editor (window.tiptap).
 * Returns 0 if the editor is not available.
 */
export function getEditorWordCount(): number {
  try {
    const text = window.tiptap?.getText?.();
    if (!text) return 0;
    return countWordsFromText(text);
  } catch {
    return 0;
  }
}

export interface FeedbackRequest {
  documentId: string;
  model: string;
  selectedCategory?: string | null;
  feedbackTypeNames?: string[];
  feedbackFingerprint?: string;
}

export interface FeedbackQuote {
  estimate: CreditEstimate;
  request: FeedbackRequest;
  requestKey: string;
  validityRevision: number;
}

/** Quotes are fetched on demand, never polled: preparing examples can use embeddings. */
export function useFeedbackCreditEstimate({
  documentId,
  project,
  selectedFeedbackCategory,
}: {
  documentId?: string;
  project?: { id?: string | null; data?: { feedbackPreferences?: unknown } } | string | null;
  selectedFeedbackCategory: string | null;
}) {
  const feedbackModel = useStoryBibleStore((state) => state.feedbackModel);
  const saved = useStoryBibleStore((state) => state.saved);
  const editor = useCommentStore((state) => state.tiptapEditor);
  const { installations, error: installationsError } = useEditPluginInstallations();
  const settingsError = Array.isArray(installations) ? null : new FeedbackSettingsUnavailableError(Boolean(installationsError));
  const preferences = typeof project === "object" ? project?.data?.feedbackPreferences : undefined;
  const projectId = typeof project === "string" ? project : project?.id;
  const request: FeedbackRequest = {
    documentId: documentId || "",
    model: feedbackModel,
    ...getFeedbackSelectionPayload({
      feedbackTypeNamesOverride: undefined,
      selectedFeedbackCategory,
      installations,
      feedbackPreferences: preferences,
    }),
  };
  const requestKey = JSON.stringify({ projectId, request, preferences, installations });
  const currentKey = useRef(requestKey);
  currentKey.current = requestKey;
  const [quote, setQuote] = useState<FeedbackQuote | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revision = useRef(0);
  const [validityRevision, setValidityRevision] = useState(0);
  const requestOwner = useRef<symbol | null>(null);
  const mounted = useRef(true);
  const invalidate = useCallback((publish = true) => {
    revision.current += 1;
    requestOwner.current = null;
    if (publish) {
      setValidityRevision(revision.current);
      setQuote(null);
      setError(null);
      setIsLoading(false);
    }
  }, []);

  // Revoke approvals on save, selection, or asynchronously attached/replaced editors.
  useLayoutEffect(() => {
    mounted.current = true;
    invalidate();
    return () => {
      mounted.current = false;
      invalidate(false);
    };
  }, [requestKey, saved, editor, invalidate]);
  useLayoutEffect(() => {
    const onUpdate = () => invalidate();
    editor?.on?.("update", onUpdate);
    return () => editor?.off?.("update", onUpdate);
  }, [editor, invalidate]);

  const isRevisionCurrent = (value: number) =>
    mounted.current && revision.current === value && currentKey.current === requestKey && editor === useCommentStore.getState().tiptapEditor;
  const isQuoteCurrent = (value: FeedbackQuote) => isRevisionCurrent(value.validityRevision) && value.requestKey === currentKey.current;

  const getFreshQuote = async (): Promise<FeedbackQuote | null> => {
    const capturedRevision = revision.current;
    const owner = Symbol();
    requestOwner.current = owner;
    const ownsRequest = () => requestOwner.current === owner && isRevisionCurrent(capturedRevision);
    setIsLoading(true);
    setError(null);
    try {
      if (!request.documentId) throw new Error("No document selected");
      // Undefined means settings have not loaded, not an empty selection. Quoting
      // now could omit project overrides, or be discarded when SWR fills the cache.
      if (settingsError) throw settingsError;
      const result = await Auth.fetchWithIdToken("/api/estimator/feedback", { ...request });
      if (!ownsRequest()) return null;
      const estimate = estimateFeedbackCredits(result);
      if (!estimate.isValid || !result.feedbackFingerprint || result.documentId !== request.documentId || result.model !== request.model) {
        throw new Error("Invalid Feedback estimate");
      }
      const next = { estimate, request: { ...request, feedbackFingerprint: result.feedbackFingerprint }, requestKey, validityRevision: capturedRevision };
      setQuote(next);
      return next;
    } catch (error) {
      if (!ownsRequest()) return null;
      setError(getFeedbackEstimateErrorMessage(error));
      throw error;
    } finally {
      // An obsolete completion must never release a replacement request's loading state.
      if (ownsRequest()) {
        requestOwner.current = null;
        setIsLoading(false);
      }
    }
  };
  const estimate: CreditEstimate = quote && isQuoteCurrent(quote) ? quote.estimate : { totalCredits: 0, creditsPerCategory: 0, isValid: false, chunkCount: 0 };
  return {
    ...estimate,
    formattedEstimate: estimate.isValid ? formatCreditEstimate(estimate.totalCredits) : "",
    enabledCategoryCount: useMemo(() => countEnabledCategories(installations as FeedbackPlugin[] | undefined), [installations]),
    wordCount: getEditorWordCount(),
    requestKey,
    validityRevision,
    settingsUnavailableMessage: settingsError?.message,
    // Editor events invalidate synchronously, before React publishes validityRevision.
    getValidityRevision: () => revision.current,
    isRevisionCurrent,
    isQuoteCurrent,
    getFreshQuote,
    isLoading,
    error,
  };
}

export default useFeedbackCreditEstimate;
